import React, { useState } from "react";
import { MapPin } from "lucide-react";
import { POI_TYPES } from "../../../../../../../constants/poiTypes";

const POIsTab = ({ pois, map, visibilityState, onVisibilityChange }) => {
  const [filters, setFilters] = useState({
    type: 'all',
    search: '',
    dimensions: {
      minHeight: '',
      maxHeight: '',
      minWidth: '',
      maxWidth: ''
    },
    route: 'all'
  });
  
  // Get unique routes from POIs
  const routes = React.useMemo(() => {
    const routeIds = new Set();
    pois.forEach(poi => {
      if (poi.routeId) routeIds.add(poi.routeId);
    });
    return Array.from(routeIds);
  }, [pois]);
  
  // Filter POIs based on criteria
  const filteredPOIs = React.useMemo(() => {
    return pois.filter(poi => {
      // Filter by type
      if (filters.type !== 'all' && poi.type !== filters.type) return false;
      
      // Filter by search term
      if (filters.search && !poi.notes?.toLowerCase().includes(filters.search.toLowerCase()) && 
          !poi.address?.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }
      
      // Filter by dimensions
      if (filters.dimensions.minHeight && poi.measurements) {
        const heightMeasurement = poi.measurements.find(m => m.label === 'height' || m.label === 'Height');
        if (!heightMeasurement || heightMeasurement.value < parseFloat(filters.dimensions.minHeight)) {
          return false;
        }
      }
      
      if (filters.dimensions.maxHeight && poi.measurements) {
        const heightMeasurement = poi.measurements.find(m => m.label === 'height' || m.label === 'Height');
        if (!heightMeasurement || heightMeasurement.value > parseFloat(filters.dimensions.maxHeight)) {
          return false;
        }
      }
      
      if (filters.dimensions.minWidth && poi.measurements) {
        const widthMeasurement = poi.measurements.find(m => m.label === 'width' || m.label === 'Width');
        if (!widthMeasurement || widthMeasurement.value < parseFloat(filters.dimensions.minWidth)) {
          return false;
        }
      }
      
      if (filters.dimensions.maxWidth && poi.measurements) {
        const widthMeasurement = poi.measurements.find(m => m.label === 'width' || m.label === 'Width');
        if (!widthMeasurement || widthMeasurement.value > parseFloat(filters.dimensions.maxWidth)) {
          return false;
        }
      }
      
      // Filter by route
      if (filters.route !== 'all' && poi.routeId !== filters.route) return false;
      
      return true;
    });
  }, [pois, filters]);

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <h3 className="text-white font-medium">Points of Interest</h3>
        
        {/* Filter Controls */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="bg-gray-700 border-gray-600 text-white text-sm rounded-lg p-1 flex-1"
            >
              <option value="all">All Types</option>
              {Object.keys(POI_TYPES).map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            
            <select
              value={filters.route}
              onChange={(e) => setFilters(prev => ({ ...prev, route: e.target.value }))}
              className="bg-gray-700 border-gray-600 text-white text-sm rounded-lg p-1 flex-1"
            >
              <option value="all">All Routes</option>
              {routes.map(routeId => (
                <option key={routeId} value={routeId}>Route {routeId.slice(-4)}</option>
              ))}
            </select>
          </div>
          
          <input
            type="text"
            placeholder="Search POIs..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            className="w-full bg-gray-700 border-gray-600 text-white text-sm rounded-lg p-2"
          />
          
          <details className="bg-gray-700/50 rounded-lg p-2">
            <summary className="text-sm text-gray-300 cursor-pointer">Dimension Filters</summary>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <label className="text-xs text-gray-400 block mb-1">Min Height</label>
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.dimensions.minHeight}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    dimensions: { ...prev.dimensions, minHeight: e.target.value }
                  }))}
                  className="w-full bg-gray-700 border-gray-600 text-white text-sm rounded-lg p-1"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Max Height</label>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.dimensions.maxHeight}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    dimensions: { ...prev.dimensions, maxHeight: e.target.value }
                  }))}
                  className="w-full bg-gray-700 border-gray-600 text-white text-sm rounded-lg p-1"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Min Width</label>
                <input
                  type="number"
                  placeholder="Min"
                  value={filters.dimensions.minWidth}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    dimensions: { ...prev.dimensions, minWidth: e.target.value }
                  }))}
                  className="w-full bg-gray-700 border-gray-600 text-white text-sm rounded-lg p-1"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 block mb-1">Max Width</label>
                <input
                  type="number"
                  placeholder="Max"
                  value={filters.dimensions.maxWidth}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    dimensions: { ...prev.dimensions, maxWidth: e.target.value }
                  }))}
                  className="w-full bg-gray-700 border-gray-600 text-white text-sm rounded-lg p-1"
                />
              </div>
            </div>
          </details>
        </div>
      </div>

      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
        {filteredPOIs.length === 0 ? (
          <div className="text-center py-4 text-gray-400">
            No POIs match the current filters
          </div>
        ) : (
          filteredPOIs.map((poi) => {
          const poiType = POI_TYPES[poi.type] || POI_TYPES.Custom;
          return (
            <div
              key={poi.id}
              className="flex items-center justify-between p-2 bg-gray-800 rounded-lg"
            >
              <div className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: poiType.color }}
                />
                <MapPin className="w-4 h-4 text-gray-400" />
                <span className="text-white">{poi.id}</span>
              </div>
            </div>
          );
        }))}
      </div>
    </div>
  );
};

export default POIsTab;